import { Routes } from '@angular/router';
import { AuthGuard } from '@guards/auth.guard';
import { LoginComponent } from './components/login/login-form/login-form.component';
import { ForgotPasswordComponent } from './components/login/forgot-password/forgot-password.component';
import { IndexComponent } from './components/layout/index/index.component';
import { ResetPasswordComponent } from './components/login/reset-password/reset-password.component';
import { TenantSelectComponent } from './components/login/tenant-select/tenant-select.component';
import { TenantGuard } from '@guards/tenant.guard';
import { TenantComponent } from './components/layout/tenant/tenant.component';
import { AcceptOrgInvitationComponent } from './areas/organization/accept-org-invite/accept-org-invitation.component';
import { NoAccessComponent } from './components/no-access/no-access.component';
import { Permission } from './_enums/permission';
import { ThankYouComponent } from './components/thank-you/thank-you.component';
import { PermissionsComponentGuard } from './_guards/permissions/permissions-component.guard';
import { PermissionsModuleGuard } from './_guards/permissions/permissions-module.guard';
import { ErrorComponent } from './components/error/error.component';
import { StoreGuard } from '@app/_store/guards/store.guard';
import { OAuthComponent } from './components/oauth/oauth.component';



export const AppRoutes: Routes = [

  {
    path: '',
    canActivate: [AuthGuard, StoreGuard],
    canActivateChild: [AuthGuard],
    component: IndexComponent,
    data: {
          store: [
            { name: 'availableColumns'},
            { name: 'costCentres'},
            { name: 'systemSettings'}
          ]
    },
    children: [
      {
        path: '',
        canActivate: [PermissionsModuleGuard, StoreGuard],
        pathMatch: 'full',
        title: 'AVUX',
        data: {
          permissions: [
            Permission.CompanyPermissions
          ],
        },
        loadChildren: () => import('@app/areas/dashboard/dashboard.module').then(m => m.DashboardModule),
      },
      {
        path: 'error',
        component: ErrorComponent,
        data: { breadcrumb: 'common.errors.error' }
      },
      {
        path: 'no-access',
        component: NoAccessComponent,
        data: { breadcrumb: 'common.errors.no_permission' }
      },
      {
        path: 'estate',
        canActivate: [PermissionsModuleGuard, StoreGuard],
        canActivateChild: [PermissionsComponentGuard],
        canDeactivate: [PermissionsModuleGuard],
        data: {
          breadcrumb: 'estate.estates',
          permissionsRequired: Permission.EstateMain,
          permissions: [
            Permission.EstateInfo,
            Permission.PersonRegister,
            Permission.CompanyPermissions,
            Permission.EstateFiles,
            Permission.EstateDevices,
            Permission.EstateMeters,
            Permission.Invoicing,
            Permission.InvoicingAdditionalInfo,
            Permission.Items,
            Permission.CompanyUnits,
            Permission.ItemPriceLists,
            Permission.AssignmentItems,
            Permission.PositioningMain,
            Permission.AssignmentInfo,
            Permission.AssignmentWorkersShown,
            Permission.AssignmentExecutionShown,
            Permission.ConsumptionReadings,
            Permission.EstateSpaceRoles,
            Permission.EstateAdditionalData,
            Permission.EstateAssignmentNote,
            Permission.AssignmentMain,
            Permission.MaintenanceBooks,
            Permission.EstateInvoicing,
            Permission.Folders,
            Permission.EstateFolderManagement,
            Permission.EstateHiddenFolders,
            Permission.EstateRoles,
            Permission.EstateDescriptionShown,
            Permission.EstateInfoShown,
            Permission.PersonMain,
            Permission.ConsumptionMain,
            Permission.EstateBookings,
            Permission.EstateReferenceNotices,
            Permission.ReservableSpace,
          ],
          store: [
            { name: 'companies'},
            { name: 'countries'},
            { name: 'maintenanceAreas'},
            { name: 'municipalities'},
            { name: 'moveNotificationCount' },
            { name: 'factoringContracts' },
            { name: 'systemSettings' },
            { name: 'externalInvoicers' },
            { name: 'maintenanceAgreements'},
            { name: 'customership'},
            { name: 'dutyAreas'},
            { name: 'snowDutyAreas'},
            { name: 'companyUnits'},
            { name: 'onlineInvoicingOperators'},
            { name: 'roomCount'}
          ]
        },
        loadChildren: () => import('@app/areas/estate/estate.module').then(m => m.EstateModule),
      },
      {
        path: 'forms',
        canActivate: [PermissionsModuleGuard, StoreGuard],
        canActivateChild: [PermissionsComponentGuard],
        canDeactivate: [PermissionsModuleGuard],
        data: {
          breadcrumb: 'forms.forms',
          permissionsRequired: Permission.EstateMain,
          permissions: [
            Permission.EstateInfo,
            Permission.PersonRegister,
            Permission.CompanyPermissions,
            Permission.EstateFiles,
            Permission.EstateDevices,
            Permission.EstateMeters,
            Permission.Invoicing,
            Permission.Items,
            Permission.ItemPriceLists,
            Permission.AssignmentItems,
            Permission.PositioningMain,
            Permission.AssignmentInfo,
            Permission.ConsumptionReadings,
            Permission.EstateSpaceRoles,
            Permission.EstateAdditionalData,
            Permission.EstateAssignmentNote,
            Permission.AssignmentMain,
            Permission.MaintenanceBooks,
            Permission.EstateInvoicing,
            Permission.Folders,
            Permission.EstateFolderManagement,
            Permission.EstateHiddenFolders,
            Permission.PersonMain,
            Permission.ConsumptionMain
          ],
          store: [
            { name: 'companies'},
            { name: 'formTypes'},
            { name: 'formScoreTypes'},
            { name: 'formModels'},
          ]
        },
        loadChildren: () => import('@app/areas/forms/forms.module').then(m => m.FormsModule),
      },
      {
        path: 'assignment',
        canActivate: [PermissionsModuleGuard, StoreGuard],
        canActivateChild: [PermissionsComponentGuard],
        canDeactivate: [PermissionsModuleGuard],
        data: {
          breadcrumb: 'assignment.assignments',
          permissionsRequired: Permission.AssignmentMain,
          permissions: [
            Permission.AssignmentInfo,
            Permission.AssignmentFromTemplate,
            Permission.AssignmentProjectInfo,
            Permission.AssignmentProjectAssignments,
            Permission.AssignmentItems,
            Permission.AssignmentFiles,
            Permission.AssignmentInvoicing,
            Permission.AssignmentMessageLog,
            Permission.AssignmentInternalComment,
            Permission.AssignmentStateLog,
            Permission.AssignmentMassActions,
            Permission.AssignmentItemEditOthers,
            Permission.AssignmentTemplate,
            Permission.AssignmentOwnList,
            Permission.AssignmentInterface,
            Permission.AssignmentComplete,
            Permission.AssignmentComments,
            Permission.AssignmentCommentModeration,
            Permission.AssignmentOrdererFromPersonRegister,
            Permission.AssignmentEndWorkForAnother,
            Permission.AssignmentMarkHandled,
            Permission.AssignmentReferral,
            Permission.AssignmentWorkOrder,
            Permission.AssignmentPerformedWork,
            Permission.AssignmentReportShown,
            Permission.AssignmentWorkersShown,
            Permission.AssignmentExecutionShown,
            Permission.AssignmentAuditShown,
            Permission.AssignmentOrdererShown,
            Permission.AssignmentOrderNumberShown,
            Permission.AssignmentProjectNumberShown,
            Permission.AssignmentReferenceRolesShown,
            Permission.AssignmentMarkToBeInvoiced,
            Permission.AssignmentMarkReversedVat,
            Permission.AssignmentClone,
            Permission.AssignmentChain,
            Permission.AssignmentAddExternalWorker,
            Permission.AssignmentReminder,
            Permission.EstateInfo,
            Permission.EstateDevices,
            Permission.EstateAssignmentNote,
            Permission.EstateReferenceNotices,
            Permission.AssignmentInvoiced,
            Permission.EstateMain,
            Permission.Items,
            Permission.InterfaceAdmin,
            Permission.InterfaceTampuuriCompleteAssignment,
            Permission.InterfaceTampuuriAddCompletedAssignment,
            Permission.InterfaceTampuuriAddInCompletedAssignment,
            Permission.InterfaceHausviseCompleteAssignment,
            Permission.InterfaceHausviseAddCompletedAssignment,
            Permission.InterfaceHausviseAddInCompletedAssignment,
            Permission.InterfaceHausviseModifyCompanyId,
            Permission.InterfaceBuennoMarkSend,
            Permission.AssignmentDiary,
            Permission.PurchaseOrderRegister,
            Permission.PurchaseOrderLinkRows,
            Permission.WorkCalendar,
            Permission.AssignmentClose,
            Permission.AssignmentToBeInvoicedInfo,
            Permission.AssignmentWorkerManagement,
            Permission.InvoicingAdditionalInfo,
          ],
          store: [
            { name: 'assignmentEntranceTypes'},
            { name: 'moveNotificationCount' },
            { name: 'assignmentTypes'},
            { name: 'factoringContracts' },
            { name: 'assignmentWaitType'},
            { name: 'assignmentUrgencyTypes'},
            { name: 'maintenanceAreas'}, // assignment-target-search(?) does it even need this
            { name: 'companies'},
            { name: 'maintenanceAgreements'},
            { name: 'customership'},
            { name: 'assignmentClasses'},
            { name: 'dutyAreas'},
            { name: 'companyUnits'},
            { name: 'companyTypes'},
            { name: 'onlineInvoicingOperators'},
            { name: 'snowDutyAreas'},
            { name: 'systemSettings'},
          ]
        },
        loadChildren: () => import('@app/areas/assignment/assignment.module').then(m => m.AssignmentModule),
      },
      {
        path: 'maintenancebook',
        title: 'AVUX',
        canActivate: [PermissionsModuleGuard, StoreGuard],
        canActivateChild: [PermissionsComponentGuard],
        canDeactivate: [PermissionsModuleGuard],
        data: {
          breadcrumb: 'maintenance_book.maintenance_books',
          permissionsRequired: Permission.MaintenanceBookMain,
          permissions: [
            Permission.MaintenanceBookTemplates,
            Permission.MaintenanceBooks,
            Permission.MaintenanceBookServiceDescription,
            Permission.MaintenanceBookYearlySummary,
            Permission.MaintenanceBookCopyFromTemplate,
            Permission.MaintenanceBookCopyFromEstate,
            Permission.AssignmentReminder,
            Permission.AssignmentItems,
            Permission.EstateInfo,
            Permission.EstateAssignmentNote,
            Permission.AssignmentOrdererFromPersonRegister,
            Permission.Items,
            Permission.AssignmentInfo,
            Permission.EstateMain
          ],
          store: [
            { name: 'assignmentEntranceTypes'},
            { name: 'assignmentUrgencyTypes'},
            { name: 'assignmentClasses'},
            { name: 'systemRoles'},
            { name: 'deadlines'},
            { name: 'moveNotificationCount' },
            { name: 'assignmentTypes'}
          ]
        },
        loadChildren: () => import('@app/areas/maintenance-book/maintenance-book.module').then(m => m.MaintenanceBookModule)
      },
      {
        path: 'my-account',
        title: 'AVUX',
        data: {
          breadcrumb: 'user.details'
        },
        loadChildren: () => import('@app/areas/user/components/my-account/my-account.module').then(m => m.MyAccountModule)
      },
      {
        path: 'org-management',
        title: 'AVUX',
        data: {
          breadcrumb: 'organization.organization_management',
          // Permissions?
        },
        loadChildren: () => import('@app/areas/organization/organization.module').then(m => m.OrganizationModule)
      },
      {
        path: 'consumption',
        title: 'AVUX',
        canActivate: [PermissionsModuleGuard, StoreGuard],
        canActivateChild: [PermissionsComponentGuard],
        canDeactivate: [PermissionsModuleGuard],
        data: {
          breadcrumb: 'consumption.consumption',
          permissionsRequired: Permission.ConsumptionMain,
          permissions: [
            Permission.ConsumptionReadings,
            Permission.ConsumptionDegreeDays,
            Permission.ConsumptionAlertLimit,
            Permission.ConsumptionReadOrder,
            Permission.ConsumptionAdministration,
            Permission.ReportMeterConsumption,
            Permission.ReportMeterConsumptionNormalized,
            Permission.ReportConsumptionSummary,
            Permission.ReportConsumptionSummaryThree,
            Permission.PersonRegister,
            Permission.InterfaceLogs,
          ],
          store: [
            { name: 'moveNotificationCount' },
            {name: 'municipalities'}
          ]
        },
        loadChildren: () => import('@app/areas/consumption/consumption.module').then(m => m.ConsumptionModule)
      },
      {
        path: 'person',
        title: 'AVUX',
        canActivate: [PermissionsModuleGuard, StoreGuard],
        canActivateChild: [PermissionsComponentGuard],
        canDeactivate: [PermissionsModuleGuard],
        data: {
          breadcrumb: 'person.person_register',
          permissionsRequired: Permission.PersonMain,
          permissions: [
            Permission.PersonRegister,
            Permission.PersonRegisterSpaceRoles,
            Permission.PersonRegisterSystemRoles,
            Permission.Invoicing,
            Permission.InvoicingAdditionalInfo,
            Permission.Items,
            Permission.ItemPriceLists,
            Permission.EstateSpaceRoles,
            Permission.EstateMain,
            Permission.PersonInvoicing,
            Permission.UserManagement
          ],
          store: [
            { name: 'moveNotificationCount' },
            { name: 'companies'},
            { name: 'companyUnits'},
            { name: 'externalInvoicers'},
            { name: 'onlineInvoicingOperators'},
            { name: 'countries'},
          ]
        },
        loadChildren: () => import('@app/areas/person/person.module').then(m => m.PersonModule)
      },
      {
        path: 'notification',
        title: 'AVUX',
        canActivate: [PermissionsModuleGuard],
        canActivateChild: [PermissionsComponentGuard],
        canDeactivate: [PermissionsModuleGuard],
        data: {
          breadcrumb: 'notification.notifications',
          permissionsRequired: Permission.NotificationMain,
          permissions: [
            Permission.NotificationMain,
            Permission.NotificationCreate,
            Permission.NotificationModifyOwn,
            Permission.NotificationOthers
          ]
        },
        loadChildren: () => import('@app/areas/notification/notification.module').then(m => m.NotificationModule)
      },
      {
        path: 'company',
        title: 'AVUX',
        canActivate: [PermissionsModuleGuard, StoreGuard],
        canActivateChild: [PermissionsComponentGuard],
        canDeactivate: [PermissionsModuleGuard],
        data: {
          breadcrumb: 'company.companies',
          permissionsRequired: Permission.CompanyMain,
          permissions: [
            Permission.CompanyPermissions,
            Permission.PersonRegister,
            Permission.Invoicing,
            Permission.InvoicingAdditionalInfo,
            Permission.Items,
            Permission.ItemPriceLists,
            Permission.CompanyInvoicing
          ],
          store: [
            { name: 'moveNotificationCount' },
            { name: 'companies'},
            { name: 'companyTypes'},
            { name: 'assignmentTypes'},
            { name: 'assignmentClasses'},
            { name: 'companyUnits'},
            { name: 'systemSettings'},
            { name: 'externalInvoicers'},
            { name: 'onlineInvoicingOperators'},
            { name: 'countries'},
          ]
        },
        loadChildren: () => import('@app/areas/company/company.module').then(m => m.CompanyModule)
      },
      {
        path: 'report',
        title: 'AVUX',
        canActivate: [PermissionsModuleGuard, StoreGuard],
        canActivateChild: [PermissionsComponentGuard],
        canDeactivate: [PermissionsModuleGuard],
        data: {
          breadcrumb: 'report.reports',
          permissionsRequired: Permission.ReportMain,
          permissions: [
            Permission.PublicationRelease
          ],
          store: [
            { name: 'maintenanceAgreements'},
            { name: 'customership'},
            { name: 'assignmentClasses'},
            { name: 'dutyAreas'},
            { name: 'companyUnits'},
            { name: 'companyTypes'},
            { name: 'snowDutyAreas'},
            { name: 'moveNotificationCount' },
            { name: 'systemSettings' },
            {name: 'assignmentTypes'},
            {name: 'maintenanceAreas'}
          ]
        },
        loadChildren: () => import('@app/areas/report/report.module').then(m => m.ReportModule)
      },
      {
        path: 'vacations',
        title: 'AVUX',
        canActivate: [PermissionsModuleGuard, StoreGuard],
        canActivateChild: [PermissionsComponentGuard],
        canDeactivate: [PermissionsModuleGuard],
        data: {
          breadcrumb: 'vacations.title',
          permissionsRequired: Permission.WorkTimeVacation,
          permissions: [
            Permission.WorkTimeVacation
          ],
          store: [
            { name: 'moveNotificationCount' },
            { name: 'vacationTypes' },
            { name: 'companies' }
          ]
        },
        loadChildren: () => import('@app/areas/vacation/vacation.module').then(m => m.VacationModule)
      },
      {
        path: 'work-time',
        title: 'AVUX',
        canActivate: [PermissionsModuleGuard, StoreGuard],
        canActivateChild: [PermissionsComponentGuard],
        canDeactivate: [PermissionsModuleGuard],
        data: {
          breadcrumb: 'work_time.work_time',
          permissionsRequired: Permission.WorkTimeMain,
          permissions: [
            Permission.WorkTimeRecords,
            Permission.WorkTimeBreakTypes,
            Permission.WorkTimeHourTypes,
            Permission.WorkTimeWorkingHours,
          ],
          store: [
            { name: 'moveNotificationCount' },
            { name: 'systemSettings' },
            { name: 'costCentres' },
            { name: 'companies' }
          ]
        },
        loadChildren: () => import('@app/areas/work-time/work-time.module').then(m => m.WorkTimeModule)
      },
      {
        path: 'work-calendar',
        title: 'AVUX',
        canActivate: [PermissionsModuleGuard, StoreGuard],
        canActivateChild: [PermissionsComponentGuard],
        canDeactivate: [PermissionsModuleGuard],
        data: {
          breadcrumb: 'work_calendar.work_calendar',
          permissions: [
            Permission.WorkCalendar
          ],
          store: [
            { name: 'moveNotificationCount' },
            { name: 'assignmentTypes'},
            { name: 'systemSettings' },
            { name: 'assignmentUrgencyTypes'}
          ]
        },
        loadChildren: () => import('@app/areas/work-calendar/work-calendar.module').then(m => m.WorkCalendarModule)
      },
      {
        path: 'positioning',
        title: 'AVUX',
        canActivate: [PermissionsModuleGuard],
        canActivateChild: [PermissionsComponentGuard],
        canDeactivate: [PermissionsModuleGuard],
        data: {
          breadcrumb: 'positioning.positioning',
          permissions: [
            Permission.PositioningMain,
            Permission.WorkMonitoring,
            Permission.AssignmentInfo
          ]
        },
        loadChildren: () => import('@app/areas/positioning/positioning.module').then(m => m.PositioningModule)
      },
      {
        path: 'publication',
        title: 'AVUX',
        canActivate: [PermissionsModuleGuard, StoreGuard],
        canActivateChild: [PermissionsComponentGuard],
        canDeactivate: [PermissionsModuleGuard],
        data: {
          breadcrumb: 'publication.automatic_publications',
          permissionsRequired: Permission.PublicationMain,
          permissions: [
            Permission.Publication,
            Permission.PublicationAll,
            Permission.PublicationRelease,
            Permission.PersonRegister
          ],
          store: [
            { name: 'moveNotificationCount' },
            { name: 'assignmentTypes'},
            { name: 'maintenanceAreas'},
          ]
        },
        loadChildren: () => import('@modules/publication/publication.module').then(m => m.PublicationModule)
      },
      {
        path: 'move-notification',
        title: 'AVUX',
        canActivate: [PermissionsModuleGuard, StoreGuard],
        canActivateChild: [PermissionsComponentGuard],
        canDeactivate: [PermissionsModuleGuard],
        data: {
          breadcrumb: 'move_notification.move_notifications',
          store: [
            { name: 'moveNotificationCount' },
          ],
          permissionsRequired: Permission.MoveNotificationMain,
          permissions: [
            Permission.MoveNotification,
            Permission.ConsumptionReadings,
            Permission.AssignmentInfo,
            Permission.EstateMain
          ],
        },
        loadChildren: () => import('@modules/move-notification/move-notification.module').then(m => m.MoveNotificationModule)
      },
      {
        path: 'invoicing-run',
        title: 'AVUX',
        canActivate: [PermissionsModuleGuard, StoreGuard],
        canActivateChild: [PermissionsComponentGuard],
        canDeactivate: [PermissionsModuleGuard],
        data: {
          breadcrumb: 'invoicing.invoicing_run.invoicing_run',
          permissionsRequired: Permission.Invoicing,
          permissions: [Permission.EstateMain,
            Permission.PurchaseOrderRegister,
            Permission.InvoicingContract,
            Permission.Items,
            Permission.PurchaseOrderLinkRows,
          ],
          store: [
            { name: 'companies' },
            { name: 'companyUnits' },
            { name: 'maintenanceAreas' },
            { name: 'assignmentTypes' },
          ],
        },
        loadChildren: () => import('@app/areas/invoicing/invoicing.module').then(m => m.InvoicingModule)
      },
      {
        path: 'administration',
        title: 'AVUX',
        canActivate: [PermissionsModuleGuard, StoreGuard],
        canActivateChild: [PermissionsComponentGuard],
        canDeactivate: [PermissionsModuleGuard],
        data: {
          breadcrumb: 'common.administration',
          permissionsRequired: Permission.AdministrationMain,
          permissions: [
            Permission.Settings,
            Permission.UserManagement,
            Permission.Params,
            Permission.AdditionalDataFields,
            Permission.AssignmentTypes,
            Permission.Permissions,
            Permission.CompanyUnits,
            Permission.Folders,
            Permission.Items,
            Permission.ItemPriceLists,
            Permission.SystemUserSettings,
            Permission.BuildingStandardTerm,
            Permission.PersonMain,
            Permission.PersonRegister
          ],
          store: [
            { name: 'moveNotificationCount' },
            { name: 'assignmentTypes' },
            { name: 'companies'},
            { name: 'companyUnits'},
            { name: 'assignmentClasses'},
            { name: 'countries'},
            { name: 'systemSettings'},
            { name: 'maintenanceAreas'}
          ]
        },
        loadChildren: () => import('@app/areas/administration/administration.module').then(m => m.AdministrationModule)
      },
      {
        path: 'round',
        title: 'AVUX',
        canActivate: [PermissionsModuleGuard, StoreGuard],
        canActivateChild: [PermissionsComponentGuard],
        canDeactivate: [PermissionsModuleGuard],
        data: {
          breadcrumb: 'round.rounds',
          permissions: [
            Permission.AssignmentItems,
            Permission.AssignmentItemEditOthers,
            Permission.Items,
            Permission.Rounds,
            Permission.AssignmentInvoicing,
            Permission.RoundCompletion,
            Permission.RoundMasterFunctions,
            Permission.RoundOthersAssignments,
            Permission.RoundCompleteOthersAssignments,
            Permission.AssignmentDiary,
          ],
          store: [
            { name: 'moveNotificationCount' },
            { name: 'systemSettings' },
            {name: 'assignmentTypes'}
          ]
        },
        loadChildren: () => import('@app/areas/round/round.module').then(m => m.RoundModule)
      },
      {
        path: 'interface',
        title: 'AVUX',
        canActivate: [PermissionsModuleGuard, StoreGuard],
        canActivateChild: [PermissionsComponentGuard],
        canDeactivate: [PermissionsModuleGuard],
        data: {
          breadcrumb: 'interface.interfaces',
          permissionsRequired: Permission.InterfaceMain,
          permissions: [
            Permission.InterfaceAdmin,
            Permission.InterfaceLogs,
            Permission.InterfaceTargetLink,
            Permission.InterfaceHausviseCompleteAssignment,
            Permission.InterfaceHausviseAddCompletedAssignment,
            Permission.InterfaceHausviseModifyCompanyId,
          ],
          store: [
            { name: 'moveNotificationCount' },
            {name: 'assignmentTypes'}]
        },
        loadChildren: () => import('@modules/interface/interface.module').then(m => m.InterfaceModule)
      },
      {
        path: 'key-management',
        title: 'AVUX',
        canActivate: [PermissionsModuleGuard, StoreGuard],
        canActivateChild: [PermissionsComponentGuard],
        canDeactivate: [PermissionsModuleGuard],
        data: {
          breadcrumb: 'key_management.key_management',
          permissionsRequired: Permission.KeyManagement,
          permissions: [
            Permission.EstateMain,
            Permission.PersonRegister,
            Permission.KeyManagementKeys,
            Permission.KeyManagementKeyGroups,
            Permission.KeyManagementLoans,
          ],
          store: [
            { name: 'moveNotificationCount' },
            { name: 'keyTypes' },
            { name: 'keyModels' },
            { name: 'companies' },
          ]
        },
        loadChildren: () => import('@app/areas/key-management/key-management.module').then(m => m.KeyManagementModule)
      }
    ]
  },
  { path: 'login', title: 'AVUX', component: LoginComponent },
  { path: 'org-login', title: 'AVUX', component: TenantSelectComponent, canActivate: [AuthGuard] },
  { path: 'forgot-password', title: 'AVUX', component: ForgotPasswordComponent },
  { path: 'reset-password', title: 'AVUX', component: ResetPasswordComponent },
  { path: 'oauth', title: 'AVUX', component: OAuthComponent },
  { path: 'org-invitation', title: 'AVUX', component: AcceptOrgInvitationComponent },
  {
    path: 'report-popup',
    title: 'AVUX',
    canActivate: [AuthGuard, PermissionsModuleGuard, StoreGuard],
    canActivateChild: [PermissionsComponentGuard],
    canDeactivate: [PermissionsModuleGuard],
    data: {
      permissions: [
        Permission.PublicationRelease
      ],
      store: [
        { name: 'maintenanceAgreements'},
        { name: 'customership'},
        { name: 'assignmentClasses'},
        { name: 'dutyAreas'},
        { name: 'companyUnits'},
        { name: 'companyTypes'},
        { name: 'snowDutyAreas'},
        { name: 'systemSettings' },
        { name: 'moveNotificationCount' },
        { name: 'assignmentTypes'},
        { name: 'maintenanceAreas'}
      ]
    },
    loadChildren: () => import('@app/areas/report/components/report-popup/report-popup.module').then(m => m.ReportPopupModule),
  },
  {
    path: ':tenant',
    title: 'AVUX',
    canActivate: [TenantGuard],
    component: TenantComponent,
    children: [
      { path: 'fault', loadChildren: () => import('@modules/fault-notification/fault-notification.module').then(m => m.FaultNotificationModule) },
      { path: 'external-view', loadChildren: () => import('@modules/external-view/external-view.module').then(m => m.ExternalViewModule) },
      { path: 'move', loadChildren: () => import('@modules/move-notification/components/move-notification-form/move-notification-form.module').then(m => m.MoveNotificationFormModule) },
      { path: 'release', loadChildren: () => import('@modules/publication/components/publication-release-view/publication-release-view.module').then(m => m.PublicationReleaseViewModule) },
      { path: 'thank-you', component: ThankYouComponent }
    ]
  },
  // otherwise redirect to home
  { path: '**', redirectTo: '' }
];

